import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { matchPath } from 'react-router';
import Header from './Header.jsx';
import Sidebar from './Sidebar.jsx';
import SignIn from '../Components/SignIn.jsx';
import SignUp from '../Components/SignUp.jsx';
import ResetPassword from '../Components/ResetPassword';
import Students from '../pages/Students.jsx';
import Staffs from '../pages/Staffs.jsx';
import Grades from '../pages/Grades.jsx';
import Attendance from '../pages/Attendance.jsx';
import Subjects from '../pages/Subjects.jsx';
import SchoolFee from '../pages/SchoolFee.jsx';
import Communication from '../pages/Communication.jsx';
import Scheduling from '../pages/Scheduling.jsx';
import LessonPlanLibrary from '../pages/LessonPlanLibrary.jsx';
import LearningMaterials from '../pages/LearningMaterials.jsx';
import Home from '../pages/Home.jsx';
import WelcomePage from '../pages/WelcomePage.jsx';
import NoAccess from '../pages/NoAccess.jsx';
import requireAuth from '../Components/requireAuth';
import AdminPanel from '../pages/AdminPanel.jsx';
import ChangePassword from '../pages/ChangePassword.jsx';
import Withdraw from '../pages/Withdrawn.jsx';

import Logo from '../img/logo1.png'

const AppContent = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        const storedAuth = localStorage.getItem('isAuthenticated');
        return storedAuth ? JSON.parse(storedAuth) : false;
    });
    const [userRole, setUserRole] = useState(() => {
        const storedRole = localStorage.getItem('userRole');
        return storedRole || '';
    });
    const location = useLocation();

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        const checkScreenWidth = () => {
            setIsSmallScreen(window.innerWidth <= 1200);
        };

        checkScreenWidth();

        window.addEventListener('resize', checkScreenWidth);

        return () => {
            window.removeEventListener('resize', checkScreenWidth);
        };
    }, []);

    useEffect(() => {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
            if (isSmallScreen) {
                if (isSidebarOpen) {
                    sidebar.classList.add('slide-in');
                    sidebar.classList.remove('slide-out');
                    document.body.classList.add('no-scroll');
                } else {
                    sidebar.classList.add('slide-out');
                    sidebar.classList.remove('slide-in');
                    document.body.classList.remove('no-scroll');
                }
            } else {
                sidebar.classList.remove('slide-in', 'slide-out');
                sidebar.style.display = 'block';
                document.body.classList.remove('no-scroll');
            }
        }
    }, [isSmallScreen, isSidebarOpen]);

    useEffect(() => {
        setIsSidebarOpen(false);
    }, [location]);

    useEffect(() => {
        localStorage.setItem('isAuthenticated', JSON.stringify(isAuthenticated));
    }, [isAuthenticated]);

    useEffect(() => {
        localStorage.setItem('userRole', userRole);
    }, [userRole]);

    const noHeaderPaths = ['/', '/signin', '/signup', '/reset-password/:token'];
    const hideHeader = noHeaderPaths.some(pattern => matchPath(pattern, location.pathname));

    const allowedRoutes = {
        student: ['/home', '/learning-materials', '/attendance', '/change-password'],
        staff: ['/home','/learning-materials', '/students', '/grades', '/attendance', '/subjects', '/scheduling', '/lesson-plan-library'],
        admin: ['/home', '/school-fee','/learning-materials', '/communication', '/staffs', '/students', '/grades', '/attendance', '/subjects', '/scheduling', '/lesson-plan-library', '/admin-panel', '/withdraw']
    };

    const AuthenticatedRoutes = () => {
        // Redirect to /home if the current path is not allowed or is root
        if (location.pathname === '/' || !allowedRoutes[userRole]?.includes(location.pathname)) {
            return <Navigate to="/home" replace />;
        }

        return (
            <div className='container-fluid px-0' style={{ overflowX: 'hidden' }}>
                <div className='row'>
                    <div className='dashboard d-flex flex-wrap'>
                        {!hideHeader && (
                            <Header
                                isSmallScreen={isSmallScreen}
                                isSidebarOpen={isSidebarOpen}
                                toggleSidebar={toggleSidebar}
                                onSignOut={() => {
                                    setIsAuthenticated(false);
                                    setUserRole('');
                                }}
                            />
                        )}
                      
                       <div>
                        <div className='epa-logo-container'>
                       <img className="epa-logo" src={Logo} alt='logo'/>
                       </div>
                        {isAuthenticated && <Sidebar allowedRoutes={allowedRoutes[userRole] || []} />}
                        </div>

                        <div className="dashboard-Container">
                         
                            <Routes>
                                <Route path="/home" element={<Home />} />
                                <Route path="/school-fee" element={requireAuth(<SchoolFee />, isAuthenticated, ['admin'], userRole)} />
                                <Route 
                                    path="/learning-materials" 
                                    element={requireAuth(
                                        <LearningMaterials />, 
                                        isAuthenticated, 
                                        ['student', 'admin', 'staff'], 
                                        userRole
                                    )} 
                                    />
                                <Route path="/communication" element={requireAuth(<Communication />, isAuthenticated, ['admin'], userRole)} />
                                <Route path="/students" element={requireAuth(<Students />, isAuthenticated, ['admin', 'staff'], userRole)} />
                                <Route path="/grades" element={requireAuth(<Grades />, isAuthenticated, ['admin', 'staff'], userRole)} />
                                <Route path="/lesson-plan-library" element={requireAuth(<LessonPlanLibrary />, isAuthenticated, ['admin', 'staff'], userRole)} />
                                <Route path="/scheduling" element={requireAuth(<Scheduling />, isAuthenticated, ['admin', 'staff'], userRole)} />
                                <Route path="/staffs" element={requireAuth(<Staffs />, isAuthenticated, ['admin'], userRole)} />
                                <Route path="/subjects" element={requireAuth(<Subjects />, isAuthenticated, ['admin', 'staff'], userRole)} />
                                <Route path="/attendance" element={requireAuth(<Attendance />, isAuthenticated, ['admin', 'staff', 'student'], userRole)} />
                                <Route path="/withdraw" element={requireAuth(<Withdraw />, isAuthenticated, ['admin'], userRole)} />
                                <Route path="/change-password" element={requireAuth(<ChangePassword />, isAuthenticated, ['student'], userRole)} />
                                <Route path="/admin-panel" element={requireAuth(<AdminPanel />, isAuthenticated, ['admin'], userRole)} />
                                <Route path="/no-access" element={<NoAccess />} />
                            </Routes>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const UnauthenticatedRoutes = () => (
        <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/signin" element={<SignIn setIsAuthenticated={setIsAuthenticated} setUserRole={setUserRole} />} />
            <Route path="/signup" element={<SignUp setIsAuthenticated={setIsAuthenticated} setUserRole={setUserRole} />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    );

    return (
        <>
            {isAuthenticated ? <AuthenticatedRoutes /> : <UnauthenticatedRoutes />}
        </>
    );
};

export default AppContent;